// Granular GDPR cookie-consent data layer, independent of any UI. Necessary cookies are not a
// stored choice (always on, can't be disabled). Analytics/marketing default OFF until the
// visitor actively decides (opt-in, not opt-out). Any analytics/marketing script must be gated
// behind hasConsent(...)/onConsentChange(...) — nothing loads unconditionally.

export type ConsentCategory = "analytics" | "marketing";
export type ConsentChoices = { analytics: boolean; marketing: boolean };
export type ConsentRecord = { choices: ConsentChoices; decidedAt: string };

const STORAGE_KEY = "cookie-consent-v2";

type Listener = (record: ConsentRecord | null) => void;
const listeners = new Set<Listener>();

function isValidChoices(value: unknown): value is ConsentChoices {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as ConsentChoices).analytics === "boolean" &&
    typeof (value as ConsentChoices).marketing === "boolean"
  );
}

export function getConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.decidedAt !== "string" || !isValidChoices(parsed.choices)) return null;
    return { choices: parsed.choices, decidedAt: parsed.decidedAt };
  } catch {
    return null;
  }
}

export function saveConsent(choices: ConsentChoices): void {
  const record: ConsentRecord = { choices, decidedAt: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage nedostupné — ignoruj, aspoň informujeme poslucháčov o zmene v rámci session */
  }
  listeners.forEach((cb) => cb(record));
}

export function hasConsent(category: ConsentCategory): boolean {
  return getConsent()?.choices[category] ?? false;
}

export function onConsentChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
